import { config } from './env';
import { linkedInPurchase, linkedInSignUp } from './linkedin';
import { redactUrl } from './redact';

/**
 * The Google tag, loaded once when a measurement id is configured, and the
 * handful of events worth sending from the app. Nothing here is on the
 * critical path: with no id it is a no-op, and a blocked script is a silent
 * one.
 *
 * `sign_up` is the conversion an ad campaign is buying. It is sent once, the
 * moment an account is created, with the method that created it.
 *
 * Every event carries the page it happened on, and the tag reads that from
 * the address bar unless told otherwise. Three of our pages have a bearer
 * token in the address (see lib/redact.ts), so the tag is told otherwise:
 * the location is set, redacted, before it loads, and again on every route
 * change, and a `set` parameter overrides what the tag would read for every
 * event after it, the automatic page views included.
 */

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let loaded = false;

export function initAnalytics(): void {
  const id = config.gaMeasurementId;
  if (!id || loaded || typeof document === 'undefined') return;
  loaded = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  window.gtag('js', new Date());
  reportLocation(window.location.href);
  // send_page_view stays on: the router changes the URL without a load, and
  // GA4's enhanced measurement picks those up as history changes.
  window.gtag('config', id);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

/**
 * Where the tag believes the page is. Called before it loads and on every
 * navigation, so the address it reports is never the one with a token in it.
 * The referrer goes through the same wash: the page before this one may have
 * been the tokenized one.
 */
export function reportLocation(href: string): void {
  if (!config.gaMeasurementId) return;
  window.gtag?.('set', {
    page_location: redactUrl(href),
    page_referrer: redactUrl(document.referrer),
  });
}

export function track(event: string, params: Record<string, unknown> = {}): void {
  if (!config.gaMeasurementId) return;
  window.gtag?.('event', event, params);
}

/** An account was created. The conversion. */
export function trackSignUp(method: 'google' | 'password'): void {
  linkedInSignUp();
  track('sign_up', { method });
}

export function trackLogin(method: 'google' | 'password'): void {
  track('login', { method });
}

/** A team plan, as the thing being bought: how it is billed, how many seats, what each costs. */
export interface SeatOrder {
  interval: 'month' | 'year';
  seats: number;
  perSeatCents: number;
}

function orderParams(order: SeatOrder) {
  return {
    currency: 'USD',
    value: (order.perSeatCents * order.seats) / 100,
    items: [
      {
        item_id: `team-seat-${order.interval}`,
        item_name: `Slate Team seat, ${order.interval === 'year' ? 'yearly' : 'monthly'}`,
        price: order.perSeatCents / 100,
        quantity: order.seats,
      },
    ],
  };
}

/** Off to Stripe Checkout. */
export function trackBeginCheckout(order: SeatOrder): void {
  track('begin_checkout', orderParams(order));
}

/**
 * Back from Stripe with a completed checkout: the revenue conversion. The
 * transaction id is Stripe's checkout session, so a reload of the return page
 * cannot count the same purchase twice.
 */
export function trackPurchase(order: SeatOrder, transactionId: string): void {
  linkedInPurchase();
  track('purchase', { ...orderParams(order), transaction_id: transactionId });
}
